import React, { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, ChevronDown } from "lucide-react";

/**
 * Reusable ShowMore component that displays limited items initially
 * and shows all items in a modal when "Show More" is clicked
 * 
 * @param {Array} items - Array of items to display
 * @param {number} initialCount - Number of items to show initially (default: 2)
 * @param {Function} renderItem - Function to render each item (receives item and index)
 * @param {string} title - Modal title
 * @param {string} emptyMessage - Message when no items exist
 */
export default function ShowMoreModal({
  items = [],
  initialCount = 2,
  renderItem,
  title = "All Items",
  emptyMessage = "No items available"
}) {
  const [isOpen, setIsOpen] = useState(false);

  if (!items || items.length === 0) {
    return <span className="text-gray-500 text-sm">{emptyMessage}</span>;
  }

  const visibleItems = items.slice(0, initialCount);
  const hasMore = items.length > initialCount;

  return (
    <>
      <div className="flex flex-wrap items-center gap-1">
        {visibleItems.map((item, index) => (
          <React.Fragment key={index}>
            {renderItem(item, index)}
            {index < visibleItems.length - 1 && !hasMore && <span>,</span>}
            {index < visibleItems.length - 1 && hasMore && <span>,</span>}
          </React.Fragment>
        ))}
        {hasMore && (
          <>
            <span>,</span>
            <button
              onClick={() => setIsOpen(true)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium inline-flex items-center gap-1 hover:underline"
            >
              +{items.length - initialCount} more
              <ChevronDown className="h-3 w-3" />
            </button>
          </>
        )}
      </div>

      <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <Dialog.Content className="fixed left-[50%] top-[50%] z-50 max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white p-6 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]">
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {title}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  className="rounded-full p-1 hover:bg-gray-100 transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </Dialog.Close>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                {items.map((item, index) => (
                  <div
                    key={index}
                    className="px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {renderItem(item, index)}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Dialog.Close asChild>
                <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors">
                  Close
                </button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
